import * as React from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { debounce } from 'lodash'
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
} from 'antd'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useUnit } from 'effector-react'
import dayjs from 'dayjs'

import { ChargebackPayload } from '../../types'
import { CHARGEBACK_STATUSES } from '../../list/consts'
import { $wallets, getWallets, walletsMounted } from '../../walletsModel'
import {
  $transactionOptions,
  getTransactions,
  transactionsSearched,
} from '../../transactionsModel'
import { ChargebackFormData, ChargebackFormProps } from './types'

export const toChargebackPayload = (
  data: ChargebackFormData,
): ChargebackPayload => ({
  wallet: Number(data.walletId),
  dateTime: data.dateTime ? data.dateTime.toISOString() : '',
  transactionId: Number(data.transactionId),
  amount: Number(data.amount),
  currency: data.currency as string,
  chbCommission: Number(data.chbCommission),
  commissionCurrency: data.commissionCurrency as string,
  exchangeRate:
    data.exchangeRate.trim() === '' ? null : Number(data.exchangeRate),
  status: data.status,
  comment: data.comment,
})

const isNumber = (value: string) =>
  value.trim() !== '' && !Number.isNaN(Number(value))

const colProps = { xs: 24, sm: 12, md: 8 }

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Divider orientation="left" orientationMargin={0}>
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'rgba(0, 0, 0, 0.45)',
      }}
    >
      {children}
    </span>
  </Divider>
)

export const ChargebackForm: React.FC<ChargebackFormProps> = ({
  defaultValues,
  onSubmit,
  loading,
  submitLabel,
}) => {
  const wallets = useUnit($wallets)
  const isWalletsLoading = useUnit(getWallets.pending)

  const transactionOptions = useUnit($transactionOptions)
  const isTransactionsLoading = useUnit(getTransactions.pending)

  const debouncedTransactionsSearch = useCallback(
    debounce(transactionsSearched, 1000),
    [],
  )

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<ChargebackFormData>({
    mode: 'onChange',
    defaultValues: {
      walletId: null,
      dateTime: dayjs(),
      transactionId: '',
      amount: '',
      currency: null,
      chbCommission: '',
      commissionCurrency: null,
      exchangeRate: '',
      status: 'Pending',
      comment: '',
      ...defaultValues,
    },
  })

  useEffect(() => {
    walletsMounted()
    transactionsSearched('')
  }, [])

  const selectedCurrency = useWatch({ control, name: 'currency' })
  const selectedCommissionCurrency = useWatch({
    control,
    name: 'commissionCurrency',
  })
  const selectedWalletId = useWatch({ control, name: 'walletId' })
  const selectedTransactionId = useWatch({ control, name: 'transactionId' })

  const transactionCurrency = useMemo(
    () =>
      transactionOptions.find(
        (option) => option.value === String(selectedTransactionId),
      )?.currency,
    [transactionOptions, selectedTransactionId],
  )

  const selectedWallet = useMemo(
    () => wallets.find((item) => String(item.id) === String(selectedWalletId)),
    [wallets, selectedWalletId],
  )

  const currencyOptions = useMemo(
    () =>
      (selectedWallet?.localBalances ?? [])
        .filter(({ currency }) => Boolean(currency))
        .map(({ currency }) => ({
          label: currency,
          value: currency,
        })),
    [selectedWallet],
  )

  const isWalletSelected = Boolean(selectedWalletId)
  const currencyPlaceholder = isWalletSelected ? 'CUR' : 'Select a wallet first'

  useEffect(() => {
    // Пока кошелёк не найден в загруженном списке, его валюты неизвестны —
    // не сбрасываем уже выбранные значения (важно для формы редактирования).
    if (!selectedWallet) return

    const availableCurrencies = currencyOptions.map((option) => option.value)

    if (selectedCurrency && !availableCurrencies.includes(selectedCurrency)) {
      setValue('currency', null, { shouldValidate: true })
    }

    if (
      selectedCommissionCurrency &&
      !availableCurrencies.includes(selectedCommissionCurrency)
    ) {
      setValue('commissionCurrency', null, { shouldValidate: true })
    }
  }, [
    selectedWallet,
    currencyOptions,
    selectedCurrency,
    selectedCommissionCurrency,
    setValue,
  ])

  useEffect(() => {
    // Валюта по умолчанию — из выбранной транзакции. Подставляем только пока
    // поле пустое (ручной/сохранённый выбор не трогаем) и только если такая
    // валюта есть у выбранного кошелька (иначе её сбросит эффект выше).
    if (selectedCurrency || !transactionCurrency) return

    const isAvailableInWallet = currencyOptions.some(
      (option) => option.value === transactionCurrency,
    )
    if (!isAvailableInWallet) return

    setValue('currency', transactionCurrency, { shouldValidate: true })
  }, [transactionCurrency, selectedCurrency, currencyOptions, setValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SectionTitle>General</SectionTitle>
      <Row gutter={16}>
        <Col {...colProps}>
          <Controller
            name="walletId"
            control={control}
            rules={{ required: 'Wallet is required' }}
            render={({ field, fieldState: { error } }) => (
              <Form.Item
                label="Wallet"
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
              >
                <Select
                  {...field}
                  showSearch
                  optionFilterProp="label"
                  loading={isWalletsLoading}
                  placeholder="Select a wallet"
                  notFoundContent={
                    isWalletsLoading ? <Spin size="small" /> : null
                  }
                  style={{ width: '100%' }}
                  options={wallets.map((wallet) => ({
                    label: wallet.name,
                    value: String(wallet.id),
                  }))}
                />
              </Form.Item>
            )}
          />
        </Col>
        <Col {...colProps}>
          <Controller
            name="transactionId"
            control={control}
            rules={{
              required: 'Transaction ID is required',
              validate: (value) =>
                /^\d+$/.test(String(value ?? '').trim()) || 'Integer number',
            }}
            render={({ field, fieldState: { error } }) => (
              <Form.Item
                label="Transaction ID"
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
              >
                <Select
                  {...field}
                  showSearch
                  filterOption={false}
                  loading={isTransactionsLoading}
                  placeholder="E.g. 100234"
                  notFoundContent={
                    isTransactionsLoading ? <Spin size="small" /> : null
                  }
                  onSearch={debouncedTransactionsSearch}
                  style={{ width: '100%' }}
                  options={transactionOptions}
                />
              </Form.Item>
            )}
          />
        </Col>
        <Col {...colProps}>
          <Controller
            name="dateTime"
            control={control}
            rules={{ required: 'Date Time is required' }}
            render={({ field, fieldState: { error } }) => (
              <Form.Item
                label="Date & time"
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
              >
                <DatePicker
                  {...field}
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}
          />
        </Col>
        <Col {...colProps}>
          <Controller
            name="status"
            control={control}
            rules={{ required: 'Status is required' }}
            render={({ field, fieldState: { error } }) => (
              <Form.Item
                label="Status"
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
              >
                <Select
                  {...field}
                  style={{ width: '100%' }}
                  options={CHARGEBACK_STATUSES.map((status) => ({
                    label: status,
                    value: status,
                  }))}
                />
              </Form.Item>
            )}
          />
        </Col>
      </Row>

      <SectionTitle>Amounts &amp; currencies</SectionTitle>
      <Row gutter={16}>
        <Col {...colProps}>
          <Form.Item
            label="Amount"
            validateStatus={
              errors.amount || errors.currency ? 'error' : undefined
            }
            help={errors.amount?.message || errors.currency?.message}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Controller
                name="amount"
                control={control}
                rules={{
                  required: 'Amount is required',
                  validate: (value) => isNumber(value) || 'Number',
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="0.00"
                    style={{ width: '62%' }}
                  />
                )}
              />
              <Controller
                name="currency"
                control={control}
                rules={{ required: 'Currency is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    showSearch
                    disabled={!isWalletSelected}
                    placeholder={currencyPlaceholder}
                    title={
                      !isWalletSelected ? 'Select a wallet first' : undefined
                    }
                    style={{ width: '38%' }}
                    options={currencyOptions}
                  />
                )}
              />
            </Space.Compact>
          </Form.Item>
        </Col>
        <Col {...colProps}>
          <Form.Item
            label="CHB commission"
            validateStatus={
              errors.chbCommission || errors.commissionCurrency
                ? 'error'
                : undefined
            }
            help={
              errors.chbCommission?.message ||
              errors.commissionCurrency?.message
            }
          >
            <Space.Compact style={{ width: '100%' }}>
              <Controller
                name="chbCommission"
                control={control}
                rules={{
                  required: 'CHB Commission is required',
                  validate: (value) =>
                    (isNumber(value) && Number(value) >= 0) ||
                    'Number not less than zero',
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="0.00"
                    style={{ width: '62%' }}
                  />
                )}
              />
              <Controller
                name="commissionCurrency"
                control={control}
                rules={{ required: 'Commission Currency is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    showSearch
                    disabled={!isWalletSelected}
                    placeholder={currencyPlaceholder}
                    title={
                      !isWalletSelected ? 'Select a wallet first' : undefined
                    }
                    style={{ width: '38%' }}
                    options={currencyOptions}
                  />
                )}
              />
            </Space.Compact>
          </Form.Item>
        </Col>
        <Col {...colProps}>
          <Controller
            name="exchangeRate"
            control={control}
            rules={{
              validate: (value) =>
                value.trim() === '' ||
                (isNumber(value) && Number(value) > 0) ||
                'Number greater than zero',
            }}
            render={({ field, fieldState: { error } }) => (
              <Form.Item
                label={
                  selectedCurrency
                    ? `Exchange rate (${selectedCurrency} → USD)`
                    : 'Exchange rate'
                }
                tooltip="Rate used to convert the selected currency into USD"
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
              >
                <Input {...field} placeholder="1.00" />
              </Form.Item>
            )}
          />
        </Col>
      </Row>

      <SectionTitle>Comment</SectionTitle>
      <Row gutter={16}>
        <Col span={24}>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <Form.Item label="Comment">
                <Input.TextArea
                  {...field}
                  rows={4}
                  placeholder="Additional chargeback details"
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            )}
          />
        </Col>
      </Row>

      <Divider className="mt-0" />
      <Row justify="end">
        <Col>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            disabled={loading}
            loading={loading}
          >
            {submitLabel}
          </Button>
        </Col>
      </Row>
    </form>
  )
}
